// =============================================================================
// Gom các phép kiểm tra quyền của cổng QCV về một chỗ.
//
// Bối cảnh: 3 cổng (QCV/GTNS/KVC) dùng chung 1 backend và 1 bảng `user`, `role`
// chỉ là một chuỗi duy nhất, không có cột nào gắn user với cổng nào. Việc tách
// cổng hiện dựa vào mỗi cổng build riêng với REACT_APP_SCENE khác nhau.
//
// Cổng QCV có 2 loại quản trị:
//   - ROLE.ADMIN_QCV ('qcv_admin')        : quản trị viên cổng QCV, thấy MỌI lĩnh vực.
//   - 'qcv_admin_<lĩnh vực>'              : hội đồng chấm ĐÚNG 1 lĩnh vực.
//
// CẢNH BÁO khi sửa: các đoạn code cũ nhận diện hội đồng theo lĩnh vực bằng
// `role.includes('qcv_admin_')` (có gạch dưới ở cuối). Chuỗi 'qcv_admin' KHÔNG
// khớp mẫu đó, nên mọi nơi muốn tính cả quản trị viên cổng phải dùng các hàm
// dưới đây thay vì tự viết lại điều kiện.
// =============================================================================

import { ROLE } from '../constants/enums';

const QCV_FIELD_ADMIN_PREFIX = 'qcv_admin_';

// Hội đồng QCV bị bó vào 1 lĩnh vực (qcv_admin_cns, qcv_admin_cnbd, ...).
export const isQcvFieldAdmin = (role) => !!role && role.includes(QCV_FIELD_ADMIN_PREFIX);

// Lĩnh vực mà tài khoản hội đồng được giao; null nếu không bị bó lĩnh vực nào.
export const getQcvAdminField = (role) =>
  (isQcvFieldAdmin(role) ? role.replaceAll(QCV_FIELD_ADMIN_PREFIX, '') : null);

// Quản trị viên cổng QCV: toàn quyền trong cổng QCV, không bó lĩnh vực.
// (super_admin là quản trị toàn hệ thống nên đương nhiên bao hàm.)
export const isQcvPortalAdmin = (role) =>
  role === ROLE.SUPER_ADMIN || role === ROLE.ADMIN_QCV;

// Có được dùng các tính năng nghiệp vụ QCV (duyệt hồ sơ, chấm điểm thành tích)
// hay không - gồm cả quản trị viên cổng lẫn hội đồng theo lĩnh vực.
export const canReviewQcv = (role) => isQcvPortalAdmin(role) || isQcvFieldAdmin(role);

// Hội đồng BÌNH CHỌN của cổng QCV: council (dùng chung 3 cổng) hoặc council_qcv.
// Khác hẳn hội đồng theo lĩnh vực (qcv_admin_*) - nhóm này chỉ bỏ phiếu.
export const isQcvCouncil = (role) =>
  role === ROLE.COUNCIL || role === ROLE.COUNCIL_QCV;

// Được MỞ XEM hồ sơ của ứng viên khác.
// CỐ Ý rộng hơn canReviewQcv: hội đồng bình chọn cần đọc hồ sơ để bỏ phiếu,
// nhưng CHỈ XEM — không chấm điểm, không duyệt hồ sơ. Quyền chấm/duyệt vẫn là
// canReviewQcv, đừng dùng hàm này để mở nút duyệt.
export const canViewQcvProfile = (role) => canReviewQcv(role) || isQcvCouncil(role);

// Các trang quản trị mà ROLE.ADMIN_QCV được vào (ngoài các tính năng QCV ở trên).
// Quản lý người dùng & Quản lý năm tổ chức CỐ Ý không nằm ở đây: cả hai đều là
// màn dùng chung cho 3 cổng, mở ra là đụng tới dữ liệu GTNS/KVC.
export const QCV_ADMIN_ROLES = [ROLE.SUPER_ADMIN, ROLE.ADMIN_QCV];

// SỬA HỒ SƠ HỘ ỨNG VIÊN (/dang-ky-ho-so/:profileId, /thong-tin-ca-nhan/:profileId)
// — CHỈ super_admin. CỐ Ý hẹp hơn isQcvPortalAdmin: quản trị viên cổng QCV
// (qcv_admin) vẫn duyệt hồ sơ, chấm điểm, bình chọn như cũ, nhưng KHÔNG được
// đứng tên ứng viên để thêm/sửa/xoá thành tích hay thông tin cá nhân của họ.
// Dùng hàm này (không dùng isQcvPortalAdmin) cho mọi cổng vào của tính năng đó.
export const canEditCandidateProfile = (role) => role === ROLE.SUPER_ADMIN;

// Bản danh sách của canEditCandidateProfile, dùng cho `allowRoles` của router.
export const PROFILE_EDIT_ROLES = [ROLE.SUPER_ADMIN];
